import api from '../../lib/api';
import {
  GraphMonitoringBulkUpsertResponse,
  GraphMonitoringEntityDetail,
  GraphMonitoringEntityQueryRequest,
  GraphMonitoringEntityQueryResponse,
  GraphMonitoringEntitySummary,
  GraphMonitoringRuleCatalogEntry,
  GraphMonitoringTenantRuleSettings,
} from './types';
import { getDetailDisplayName, resolveMonitoringDisplayName } from './utils';
import { PortfolioDiff } from './portfolioDiffModel';

function normalizeMonitoringDetailDisplayName(
  detail: GraphMonitoringEntityDetail,
  preferredDisplayName?: string,
) {
  const normalizedDisplayName = getDetailDisplayName(detail, preferredDisplayName);

  if (
    !normalizedDisplayName ||
    detail.monitoredEntity.displayName?.trim() === normalizedDisplayName
  ) {
    return detail;
  }

  return {
    ...detail,
    monitoredEntity: {
      ...detail.monitoredEntity,
      displayName: normalizedDisplayName,
    },
  };
}

export async function fetchGraphMonitoringRules() {
  return api.get<GraphMonitoringRuleCatalogEntry[]>('/v1/graphmonitoring/rules');
}

export async function fetchPortfolioSummary(page: number, pageSize: number) {
  return api.get<PortfolioDiff>(`/v2/graphmonitoring/portfolio-summary?page=${page}&pageSize=${pageSize}`);
}

export async function fetchGraphMonitoringEntities() {
  return api.get<GraphMonitoringEntitySummary[]>('/v1/graphmonitoring');
}

export async function fetchGraphMonitoringSettings() {
  return api.get<GraphMonitoringTenantRuleSettings>('/v1/graphmonitoring/settings');
}

export async function queryGraphMonitoringEntities(
  request: GraphMonitoringEntityQueryRequest = {},
) {
  return api.post<GraphMonitoringEntityQueryResponse>('/v1/graphmonitoring/query', request);
}

export async function fetchGraphMonitoringDetail(
  orgNumber: string,
  options: { snapshotLimit?: number; diffLimit?: number; preferredDisplayName?: string } = {},
) {
  const snapshotLimit = options.snapshotLimit ?? 20;
  const diffLimit = options.diffLimit ?? 20;

  const detail = await api.get<GraphMonitoringEntityDetail>(
    `/v1/graphmonitoring/${orgNumber}?snapshotLimit=${snapshotLimit}&diffLimit=${diffLimit}`,
  );

  return normalizeMonitoringDetailDisplayName(detail, options.preferredDisplayName);
}

export function getAllGraphMonitoringRuleKeys(
  rules: GraphMonitoringRuleCatalogEntry[],
) {
  return Array.from(new Set(rules.map((rule) => rule.ruleKey).filter(Boolean))).sort(
    (left, right) => left.localeCompare(right, 'sv'),
  );
}

export async function saveGraphMonitoringSettings(
  ruleKeys: string[],
) {
  return api.put<GraphMonitoringTenantRuleSettings>(
    '/v1/graphmonitoring/settings',
    {
      ruleKeys: [...ruleKeys].sort((left, right) => left.localeCompare(right, 'sv')),
    },
  );
}

export async function enableGraphMonitoring(
  orgNumber: string,
  displayName?: string,
) {
  const trimmedDisplayName = resolveMonitoringDisplayName(displayName) || undefined;

  const detail = await api.put<GraphMonitoringEntityDetail>(
    `/v1/graphmonitoring/${orgNumber}`,
    trimmedDisplayName ? { displayName: trimmedDisplayName } : {},
  );

  return normalizeMonitoringDetailDisplayName(detail, trimmedDisplayName);
}

export async function bulkEnableGraphMonitoring(
  orgNumbers: string[],
) {
  const normalizedOrgNumbers = Array.from(
    new Set(
      orgNumbers
        .map((orgNumber) => orgNumber.trim())
        .filter(Boolean),
    ),
  );

  return api.post<GraphMonitoringBulkUpsertResponse>(
    '/v1/graphmonitoring',
    {
      orgNumbers: normalizedOrgNumbers,
    },
  );
}

export async function unsubscribeGraphMonitoring(
  orgNumber: string,
) {
  await api.delete(`/v1/graphmonitoring/${orgNumber}`);
}
